import { Button, PasswordField, useTranslation } from '@hbs-us/ui';
import { Check, X } from 'lucide-react';
import { type KeyboardEvent, useId, useState } from 'react';

import { ApiError } from '../../../lib/apiFetch.js';
import { ConfirmDialog } from '../../_money/ConfirmDialog.js';
import {
  type ServiceTitanEnvironment,
  useDeleteServiceTitanCredentialsMutation,
  useSaveServiceTitanCredentialsMutation,
  useServiceTitanConnectionsQuery,
  useTestServiceTitanConnectionMutation,
} from '../useServiceTitanQuery.js';

/**
 * The Settings "ServiceTitan" card — install and prove a tenant's ServiceTitan credentials.
 *
 * ADMIN ONLY, and rendered by the route rather than gated here: these credentials belong to the
 * whole tenant, which is a narrower audience than the Manager+ most money surfaces use. The api
 * worker enforces it again on every request — hiding this card is UX, never authorization.
 *
 * ⚠ CREDENTIALS ARE WRITE-ONLY IN THIS UI. Nothing on this screen can display a client secret or
 * an app key, because the server never returns them. An installed connection is identified by the
 * last four of its client id, which is what lets an admin tell WHICH key pair is in place without
 * anything being decrypted for display. The form fields are cleared on a successful save so the
 * plaintext does not sit in component state afterwards.
 *
 * "Test connection" reads business units rather than only minting a token: a token proves the
 * client id/secret pair alone, and a test that passed while every real call 403'd would be worse
 * than no test at all.
 */

const SECTION = 'rounded-md border border-border bg-card p-6';
const SECTION_HEADING = 'mb-1 font-display font-semibold text-foreground text-lg';
const SECTION_DESC = 'mb-4 text-muted-foreground text-sm';
const INPUT =
  'mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL = 'block font-medium text-foreground text-sm';

/** Blank form state. Also what a successful save resets to, so no secret lingers in memory. */
const EMPTY_FORM = {
  stTenantId: '',
  clientId: '',
  clientSecret: '',
  appKey: '',
};

export function ServiceTitanCard() {
  const { t } = useTranslation();
  const fieldId = useId();

  const [environment, setEnvironment] = useState<ServiceTitanEnvironment>('integration');
  const [form, setForm] = useState(EMPTY_FORM);
  const [disconnecting, setDisconnecting] = useState<ServiceTitanEnvironment | null>(null);

  const query = useServiceTitanConnectionsQuery();
  const save = useSaveServiceTitanCredentialsMutation();
  const remove = useDeleteServiceTitanCredentialsMutation();
  const test = useTestServiceTitanConnectionMutation();

  const connections = query.data?.connections ?? [];
  const encryptionAvailable = query.data?.encryptionAvailable ?? true;
  const installed = connections.find((c) => c.environment === environment) ?? null;

  const complete =
    form.stTenantId.trim() !== '' &&
    form.clientId.trim() !== '' &&
    form.clientSecret.trim() !== '' &&
    form.appKey.trim() !== '';

  function update(key: keyof typeof EMPTY_FORM, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  /**
   * ⚠ THIS CARD MUST NOT CONTAIN A `<form>`. The whole settings page is already one — `SettingsForm`
   * opens a `<form>` and every card renders inside it — so a form here would be a NESTED form, and
   * the submit button would be owned by the OUTER form rather than this one. The symptom is brutal
   * to diagnose: clicking Save submits the settings page, this handler never runs, no request is
   * ever sent, and the page appears to reload with no error anywhere. That shipped once.
   *
   * So: a plain button with onClick, which is also why StripeConnectCard beside us has no form.
   */
  function onSave() {
    if (!complete || save.isPending) return;

    save.mutate(
      { environment, ...form },
      {
        // Cleared only on success: a failed save keeps what was typed so it does not all have to
        // be pasted again.
        onSuccess: () => setForm(EMPTY_FORM),
      },
    );
  }

  /**
   * Enter inside any field saves the credentials.
   *
   * Without this the keypress triggers implicit submission of the SETTINGS form we are nested in,
   * so pressing Enter in the client-secret box would save the company profile instead — which is
   * both surprising and silent.
   */
  function onFieldKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onSave();
  }

  /**
   * The server's stable error codes are four different jobs for whoever reads them, so they get
   * four different messages rather than one "something went wrong".
   */
  function testMessage(): { tone: 'ok' | 'error'; text: string } | null {
    if (test.isPending) return null;
    if (test.isSuccess) {
      return {
        tone: 'ok',
        text: t('settings:serviceTitan.testOk', { count: test.data.businessUnitCount }),
      };
    }
    if (!test.isError) return null;

    const code = test.error instanceof ApiError ? test.error.message : '';
    const key =
      {
        servicetitan_not_configured: 'testNotConfigured',
        servicetitan_auth_rejected: 'testAuthRejected',
        // A token was minted, so the client id and secret are already proven — this message must
        // NOT send anybody back to re-paste them.
        servicetitan_api_forbidden: 'testApiForbidden',
        servicetitan_credentials_unreadable: 'testUnreadable',
        servicetitan_encryption_unavailable: 'testEncryptionUnavailable',
      }[code] ?? 'testUnavailable';

    return { tone: 'error', text: t(`settings:serviceTitan.${key}`) };
  }

  const message = testMessage();

  return (
    <section className={SECTION}>
      <h2 className={SECTION_HEADING}>{t('settings:serviceTitan.heading')}</h2>
      <p className={SECTION_DESC}>{t('settings:serviceTitan.description')}</p>

      {!encryptionAvailable ? (
        // Not the admin's fault and not fixable from this screen — say so rather than letting the
        // form be filled in and rejected on submit.
        <p className="mb-4 rounded-md border border-border bg-muted p-3 text-foreground text-sm">
          {t('settings:serviceTitan.encryptionUnavailable')}
        </p>
      ) : null}

      {/* Which ServiceTitan world these credentials belong to. Keys from one never work against
          the other, so this is part of the credential, not a display preference. */}
      <div className="mb-4 flex flex-wrap gap-2">
        {(['integration', 'production'] as const).map((env) => {
          const active = env === environment;
          const connected = connections.some((c) => c.environment === env);
          return (
            <Button
              key={env}
              type="button"
              variant={active ? 'primary' : 'secondary'}
              onClick={() => setEnvironment(env)}
            >
              {t(`settings:serviceTitan.env.${env}`)}
              {connected ? <Check className="size-4" aria-hidden="true" /> : null}
            </Button>
          );
        })}
      </div>

      {installed ? (
        <div className="mb-4 rounded-md border border-border p-3 text-sm">
          <p className="text-foreground">
            {t('settings:serviceTitan.installed', {
              last4: installed.clientIdLast4 || '••••',
              tenant: installed.stTenantId,
            })}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={test.isPending}
              onClick={() => test.mutate(environment)}
            >
              {test.isPending
                ? t('settings:serviceTitan.testing')
                : t('settings:serviceTitan.test')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setDisconnecting(environment)}>
              {t('settings:serviceTitan.disconnect')}
            </Button>
          </div>
          {message ? (
            <p
              className={`mt-3 flex items-center gap-2 text-sm ${
                message.tone === 'ok' ? 'text-foreground' : 'text-destructive'
              }`}
            >
              {message.tone === 'ok' ? (
                <Check className="size-4" aria-hidden="true" />
              ) : (
                <X className="size-4" aria-hidden="true" />
              )}
              {message.text}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={LABEL} htmlFor={`${fieldId}-st-tenant`}>
            {t('settings:serviceTitan.tenantId')}
          </label>
          <input
            id={`${fieldId}-st-tenant`}
            className={INPUT}
            value={form.stTenantId}
            autoComplete="off"
            onKeyDown={onFieldKeyDown}
            onChange={(e) => update('stTenantId', e.target.value)}
          />
        </div>
        <div>
          <label className={LABEL} htmlFor={`${fieldId}-client-id`}>
            {t('settings:serviceTitan.clientId')}
          </label>
          <input
            id={`${fieldId}-client-id`}
            className={INPUT}
            value={form.clientId}
            autoComplete="off"
            onKeyDown={onFieldKeyDown}
            onChange={(e) => update('clientId', e.target.value)}
          />
        </div>
        {/*
          Hidden by default so a shoulder-surfer or a screen share does not read either
          value while it is typed, and never re-populated from the server — both boxes are
          always blank on load. The reveal is what makes that bearable: these are long
          opaque strings pasted out of ServiceTitan, and until now there was no way to
          check a paste before saving it. No length rule is printed because ServiceTitan
          owns these, not us; the truth about them comes back from "Test connection".
        */}
        <PasswordField
          className="bg-background"
          label={t('settings:serviceTitan.clientSecret')}
          placeholder={t('settings:serviceTitan.clientSecretPlaceholder')}
          showLabel={t('common:password.show')}
          hideLabel={t('common:password.hide')}
          value={form.clientSecret}
          autoComplete="new-password"
          onKeyDown={onFieldKeyDown}
          onChange={(e) => update('clientSecret', e.target.value)}
        />
        <PasswordField
          className="bg-background"
          label={t('settings:serviceTitan.appKey')}
          placeholder={t('settings:serviceTitan.appKeyPlaceholder')}
          showLabel={t('common:password.show')}
          hideLabel={t('common:password.hide')}
          value={form.appKey}
          autoComplete="new-password"
          onKeyDown={onFieldKeyDown}
          onChange={(e) => update('appKey', e.target.value)}
        />

        <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={onSave}
            disabled={!complete || !encryptionAvailable || save.isPending}
          >
            {save.isPending
              ? t('settings:serviceTitan.saving')
              : installed
                ? t('settings:serviceTitan.replace')
                : t('settings:serviceTitan.save')}
          </Button>
          {save.isError ? (
            <span className="text-destructive text-sm">
              {t('settings:serviceTitan.saveFailed')}
            </span>
          ) : null}
          {save.isSuccess ? (
            <span className="text-muted-foreground text-sm">
              {t('settings:serviceTitan.saved')}
            </span>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={disconnecting !== null}
        heading={t('settings:serviceTitan.disconnectTitle')}
        body={t('settings:serviceTitan.disconnectBody')}
        confirmLabel={t('settings:serviceTitan.disconnect')}
        cancelLabel={t('settings:serviceTitan.disconnectCancel')}
        confirmPending={remove.isPending}
        onCancel={() => setDisconnecting(null)}
        onConfirm={() => {
          if (disconnecting) remove.mutate(disconnecting);
          setDisconnecting(null);
        }}
      />
    </section>
  );
}
